/**
 * Oath Authentication Service
 * Handles login, logout, token refresh, and user info for the new auth flow
 */

import { OathStorageKeys, SessionConfig, normalizePermissions } from '../models/userModels';

const BASE_URL = process.env.REACT_APP_BASE_URL;

/**
 * Get the user's timezone
 * @returns {string} IANA timezone string
 */
const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York';
  }
};

/**
 * Login with username and password
 * @param {string} loginId - Username
 * @param {string} password - Password
 * @returns {Promise<{success: boolean, user?: OathUserInfo, error?: string}>}
 */
export const oathLogin = async (loginId, password) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        loginId,
        password,
        timeZone: getTimeZone(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      if (response.status === 401) {
        return { success: false, error: 'Invalid username or password' };
      }
      if (response.status === 423) {
        return { success: false, error: 'Account is locked. Please contact support.' };
      }
      if (response.status === 429) {
        return { success: false, error: 'Too many login attempts. Please try again later.' };
      }

      return { success: false, error: errorData.message || 'Login failed. Please try again.' };
    }

    const data = await response.json();
    const result = data.result || data;

    // Normalize permissions from flat array (legacy) to per-agency dict
    if (result.user) {
      result.user.permissions = normalizePermissions(result.user?.permissions, result.user?.agencies);
    }

    // Store tokens and user info
    const sessionExpiry = new Date();
    sessionExpiry.setHours(sessionExpiry.getHours() + SessionConfig.SESSION_DURATION_HOURS);

    localStorage.setItem(OathStorageKeys.JWT_TOKEN, result.token);
    localStorage.setItem(OathStorageKeys.REFRESH_TOKEN, result.refreshToken);
    localStorage.setItem(OathStorageKeys.PORTAL_USER_ID, result.user.portalUserId.toString());
    localStorage.setItem(OathStorageKeys.SESSION_EXPIRY, sessionExpiry.toISOString());
    localStorage.setItem(OathStorageKeys.USER_INFO, JSON.stringify(result.user));
    // Mark fresh login so initAuth skips the /me call (avoids stale backend cache)
    sessionStorage.setItem('oathFreshLogin', Date.now().toString());

    return { success: true, user: result.user };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Refresh the JWT token
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const oathRefreshToken = async () => {
  try {
    const portalUserId = localStorage.getItem(OathStorageKeys.PORTAL_USER_ID);
    const refreshToken = localStorage.getItem(OathStorageKeys.REFRESH_TOKEN);

    if (!portalUserId || !refreshToken) {
      return { success: false, error: 'No session to refresh' };
    }

    const response = await fetch(`${BASE_URL}OathAuth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        portalUserId: parseInt(portalUserId, 10),
        refreshToken,
        timeZone: getTimeZone(),
      }),
    });

    if (!response.ok) {
      // Clear storage on refresh failure - session is invalid
      oathLogout();
      return { success: false, error: 'Session expired. Please login again.' };
    }

    const data = await response.json();
    const result = data.result || data;

    // Update tokens and session expiry
    const sessionExpiry = new Date();
    sessionExpiry.setHours(sessionExpiry.getHours() + SessionConfig.SESSION_DURATION_HOURS);

    localStorage.setItem(OathStorageKeys.JWT_TOKEN, result.token);
    localStorage.setItem(OathStorageKeys.REFRESH_TOKEN, result.refreshToken);
    localStorage.setItem(OathStorageKeys.SESSION_EXPIRY, sessionExpiry.toISOString());

    // Update user info if provided (refresh now returns full user with fresh permissions)
    if (result.user) {
      result.user.permissions = normalizePermissions(result.user?.permissions, result.user?.agencies);
      localStorage.setItem(OathStorageKeys.USER_INFO, JSON.stringify(result.user));
    }

    return { success: true, user: result.user };
  } catch (error) {
    console.error('Token refresh error:', error);
    return { success: false, error: 'Failed to refresh session' };
  }
};

/**
 * Get current user info from the server
 * @returns {Promise<{success: boolean, user?: OathUserInfo, error?: string}>}
 */
export const oathGetCurrentUser = async () => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${BASE_URL}OathAuth/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token
        const refreshResult = await oathRefreshToken();
        if (refreshResult.success) {
          // Retry the request with new token
          return oathGetCurrentUser();
        }
        return { success: false, error: 'Session expired' };
      }
      return { success: false, error: 'Failed to get user info' };
    }

    const data = await response.json();
    const user = data.result || data;

    // Preserve agencies from stored user if not returned by /me endpoint
    const storedUserInfo = localStorage.getItem(OathStorageKeys.USER_INFO);
    if (storedUserInfo) {
      const storedUser = JSON.parse(storedUserInfo);
      if (storedUser.agencies && (!user.agencies || user.agencies.length === 0)) {
        user.agencies = storedUser.agencies;
      }
    }

    // Normalize permissions from flat array (legacy) to per-agency dict
    user.permissions = normalizePermissions(user.permissions, user.agencies);

    // Update stored user info
    localStorage.setItem(OathStorageKeys.USER_INFO, JSON.stringify(user));

    return { success: true, user };
  } catch (error) {
    console.error('Get current user error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Get user permissions from the server
 * @returns {Promise<{success: boolean, permissions?: OathPermission[], error?: string}>}
 */
export const oathGetPermissions = async () => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${BASE_URL}OathAuth/permissions`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        const refreshResult = await oathRefreshToken();
        if (refreshResult.success) {
          return oathGetPermissions();
        }
        return { success: false, error: 'Session expired' };
      }
      return { success: false, error: 'Failed to get permissions' };
    }

    const data = await response.json();
    const permissions = data.result || data;

    return { success: true, permissions };
  } catch (error) {
    console.error('Get permissions error:', error);
    return { success: false, error: 'Network error' };
  }
};

/**
 * Login with an impersonation token (generated by b360 admin portal)
 * @param {string} impersonationToken - The one-time impersonation token
 * @returns {Promise<{success: boolean, user?: OathUserInfo, impersonatedBy?: string, error?: string}>}
 */
export const oathLoginWithImpersonationToken = async (impersonationToken) => {
  try {
    const response = await fetch(`${BASE_URL}oathauth/exchange-impersonation-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token: impersonationToken,
        timeZone: getTimeZone(),
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.message || 'Invalid or expired impersonation token' };
    }

    const data = await response.json();
    const result = data.result || data;

    // Normalize permissions
    if (result.user) {
      result.user.permissions = normalizePermissions(result.user?.permissions, result.user?.agencies);
    }

    // 30-minute session, no refresh token
    const sessionExpiry = new Date(Date.now() + 30 * 60 * 1000);

    localStorage.setItem(OathStorageKeys.JWT_TOKEN, result.token);
    localStorage.removeItem(OathStorageKeys.REFRESH_TOKEN);
    localStorage.setItem(OathStorageKeys.PORTAL_USER_ID, result.user.portalUserId.toString());
    localStorage.setItem(OathStorageKeys.SESSION_EXPIRY, sessionExpiry.toISOString());
    localStorage.setItem(OathStorageKeys.USER_INFO, JSON.stringify(result.user));
    localStorage.setItem('isImpersonation', 'true');
    localStorage.setItem('impersonatedBy', result.impersonatedBy || '');

    return { success: true, user: result.user, impersonatedBy: result.impersonatedBy };
  } catch (error) {
    console.error('Impersonation login error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Logout - clear all stored auth data
 */
export const oathLogout = () => {
  localStorage.removeItem(OathStorageKeys.JWT_TOKEN);
  localStorage.removeItem(OathStorageKeys.REFRESH_TOKEN);
  localStorage.removeItem(OathStorageKeys.PORTAL_USER_ID);
  localStorage.removeItem(OathStorageKeys.SESSION_EXPIRY);
  localStorage.removeItem(OathStorageKeys.USER_INFO);
  localStorage.removeItem('isImpersonation');
  localStorage.removeItem('impersonatedBy');
};

/**
 * Check if user is authenticated (has valid tokens)
 * @returns {boolean}
 */
export const oathIsAuthenticated = () => {
  const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);
  const sessionExpiry = localStorage.getItem(OathStorageKeys.SESSION_EXPIRY);

  if (!token || !sessionExpiry) {
    return false;
  }

  // Check if session has expired
  const expiryDate = new Date(sessionExpiry);
  if (expiryDate <= new Date()) {
    oathLogout();
    return false;
  }

  return true;
};

/**
 * Get stored user info from localStorage
 * @returns {OathUserInfo | null}
 */
export const oathGetStoredUser = () => {
  try {
    const userInfo = localStorage.getItem(OathStorageKeys.USER_INFO);
    if (!userInfo) return null;
    const user = JSON.parse(userInfo);
    // Normalize permissions in case stored data is stale (flat array from pre-overhaul)
    if (user && Array.isArray(user.permissions)) {
      user.permissions = normalizePermissions(user.permissions, user.agencies);
    }
    return user;
  } catch {
    return null;
  }
};

/**
 * Get stored JWT token
 * @returns {string | null}
 */
export const oathGetToken = () => {
  return localStorage.getItem(OathStorageKeys.JWT_TOKEN);
};

/**
 * Get session expiry time
 * @returns {Date | null}
 */
export const oathGetSessionExpiry = () => {
  const expiry = localStorage.getItem(OathStorageKeys.SESSION_EXPIRY);
  return expiry ? new Date(expiry) : null;
};

/**
 * Get remaining session time in milliseconds
 * @returns {number} Milliseconds remaining, or 0 if expired
 */
export const oathGetRemainingSessionTime = () => {
  const expiry = oathGetSessionExpiry();
  if (!expiry) return 0;

  const remaining = expiry.getTime() - Date.now();
  return remaining > 0 ? remaining : 0;
};

/**
 * Request a password reset email.
 *
 * Accepts either { username } OR { email, agencyId } — the backend rejects mixing or partial pairs.
 *
 * @param {{ username?: string, email?: string, agencyId?: number }} params
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const oathForgotPassword = async ({ username, email, agencyId } = {}) => {
  try {
    const payload = username
      ? { username }
      : { email, agencyId };

    const response = await fetch(`${BASE_URL}OathAuth/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to process request' };
    }

    return { success: true, message: result.message };
  } catch (error) {
    console.error('Forgot password error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Validate a password reset token
 * @param {string} token - Reset token from email
 * @returns {Promise<{success: boolean, valid: boolean, message?: string, error?: string}>}
 */
export const oathValidateResetToken = async (token) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/validate-reset-token/${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, valid: false, error: result.message || 'Failed to validate token' };
    }

    return { success: true, valid: result.valid, message: result.message };
  } catch (error) {
    console.error('Validate reset token error:', error);
    return { success: false, valid: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Reset password using token from email
 * @param {string} token - Reset token from email
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const oathResetPassword = async (token, newPassword, confirmPassword) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword, confirmPassword }),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to reset password' };
    }

    return { success: true, message: result.message };
  } catch (error) {
    console.error('Reset password error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Set password using token (first-time setup or password reset)
 * @param {string} token - Setup/reset token from email
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const oathSetPassword = async (token, newPassword, confirmPassword) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/set-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ token, newPassword, confirmPassword }),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to set password' };
    }

    return { success: true, message: result.message };
  } catch (error) {
    console.error('Set password error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Change password for authenticated user
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const oathChangePassword = async (currentPassword, newPassword, confirmPassword) => {
  try {
    const token = localStorage.getItem(OathStorageKeys.JWT_TOKEN);

    if (!token) {
      return { success: false, error: 'Not authenticated' };
    }

    const response = await fetch(`${BASE_URL}OathAuth/change-password`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ currentPassword, newPassword, confirmPassword }),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      if (response.status === 401) {
        const refreshResult = await oathRefreshToken();
        if (refreshResult.success) {
          return oathChangePassword(currentPassword, newPassword, confirmPassword);
        }
        return { success: false, error: 'Session expired. Please login again.' };
      }
      return { success: false, error: result.message || 'Failed to change password' };
    }

    return { success: true, message: result.message };
  } catch (error) {
    console.error('Change password error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Self-register as an agency's primary agent
 * @param {string} email - Email address registered with Bankers Surety
 * @param {number} agentId - Agency's Agent ID
 * @param {string} username - Chosen username
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
export const oathRegister = async (email, agentId, username) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, agentId, username }),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, error: result.message || 'Registration failed' };
    }

    return { success: true, message: result.message };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};

/**
 * Check if a username is available
 * @param {string} username - Username to check
 * @returns {Promise<{success: boolean, available?: boolean, error?: string}>}
 */
export const oathCheckUsername = async (username) => {
  try {
    const response = await fetch(`${BASE_URL}OathAuth/check-username`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username }),
    });

    const data = await response.json().catch(() => ({}));
    const result = data.result || data;

    if (!response.ok) {
      return { success: false, error: result.message || 'Failed to check username' };
    }

    return { success: true, available: result.available };
  } catch (error) {
    console.error('Check username error:', error);
    return { success: false, error: 'Network error. Please check your connection.' };
  }
};
